import React from "react";
import { Link } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import useCountUp from "../../../hooks/useCountUp";

/**
 * DASHBOARD WIDGETS
 * =================
 * Small, composable pieces the dashboard pages build from, so panels are not
 * re-implemented per page. Follows the Card.js / Button.js convention of
 * several related exports in one module.
 *
 * Colors are semantic tokens throughout, so these inherit the section accent
 * (violet in the student panel, teal in the teachers panel) automatically.
 */

/** Maps a tone name onto the token trio it implies. */
export const TONES = {
    primary: {
        text: "text-primary",
        soft: "bg-primary-soft",
        bar: "bg-primary",
    },
    success: {
        text: "text-success",
        soft: "bg-success-soft",
        bar: "bg-success",
    },
    warning: {
        text: "text-warning",
        soft: "bg-warning-soft",
        bar: "bg-warning",
    },
    danger: { text: "text-danger", soft: "bg-danger-soft", bar: "bg-danger" },
    neutral: {
        text: "text-ink-muted",
        soft: "bg-surface-sunken",
        bar: "bg-rule-interactive",
    },
};

const toneOf = (tone) => TONES[tone] ?? TONES.primary;

/**
 * A titled panel. `index` drives the staggered entrance (see .dash-rise).
 */
export function SectionCard({
    title,
    description,
    icon: Icon,
    actionLabel,
    actionTo,
    onActionClick,
    index = 0,
    className = "",
    children,
}) {
    const hasAction = actionTo || onActionClick;
    return (
        <section
            className={`dash-rise flex flex-col rounded-panel border border-rule-subtle bg-surface p-xl shadow-card ${className}`}
            style={{ "--i": index }}
        >
            {(title || hasAction) && (
                <div className="mb-lg flex items-start justify-between gap-sm">
                    <div className="flex min-w-0 items-center gap-sm">
                        {Icon && (
                            <span className="flex h-[3.2rem] w-[3.2rem] shrink-0 items-center justify-center rounded-soft bg-primary-soft text-primary">
                                <Icon sx={{ fontSize: "1.8rem" }} />
                            </span>
                        )}
                        <div className="min-w-0">
                            <h2 className="truncate text-md font-semibold text-ink">
                                {title}
                            </h2>
                            {description && (
                                <p className="truncate text-sm text-ink-subtle">
                                    {description}
                                </p>
                            )}
                        </div>
                    </div>
                    {actionTo && (
                        <Link
                            to={actionTo}
                            className="inline-flex shrink-0 items-center gap-2xs rounded-soft px-xs py-2xs text-sm text-primary transition-colors duration-150 hover:bg-primary-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                            {actionLabel}
                            <ArrowBackIosNewIcon sx={{ fontSize: "1.1rem" }} />
                        </Link>
                    )}
                    {!actionTo && onActionClick && (
                        <button
                            type="button"
                            onClick={onActionClick}
                            className="inline-flex shrink-0 items-center gap-2xs rounded-soft bg-primary-soft px-md py-2xs text-sm text-primary transition-colors duration-150 hover:bg-primary hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none"
                        >
                            {actionLabel}
                        </button>
                    )}
                </div>
            )}
            {children}
        </section>
    );
}

/**
 * A single metric. Animates the number up on mount and, when `max` is given,
 * draws a thin progress track underneath so the value has context.
 */
export function StatTile({
    label,
    value,
    max,
    suffix,
    unit,
    icon: Icon,
    tone = "primary",
    index = 0,
}) {
    const tokens = toneOf(tone);
    const animated = useCountUp(value);
    const hasValue = value !== null && value !== undefined;
    const percent =
        hasValue && max ? Math.min(Math.round((value / max) * 100), 100) : null;

    return (
        <div
            className="dash-rise group rounded-panel border border-rule-subtle bg-surface p-lg shadow-card transition-shadow duration-200 hover:shadow-headerCard motion-reduce:transition-none"
            style={{ "--i": index }}
        >
            <div className="flex items-center gap-sm">
                {Icon && (
                    <span
                        className={`flex h-[3.2rem] w-[3.2rem] shrink-0 items-center justify-center rounded-soft ${tokens.soft} ${tokens.text}`}
                    >
                        <Icon sx={{ fontSize: "1.8rem" }} />
                    </span>
                )}
                <p className="truncate text-sm text-ink-subtle">{label}</p>
            </div>

            <p className="mt-md flex items-baseline gap-2xs">
                <span
                    className={`text-3xl font-semibold tabular-nums ${tokens.text}`}
                >
                    {hasValue ? Math.round(animated).toLocaleString("fa-IR") : "—"}
                </span>
                {suffix && hasValue && (
                    <span className="text-sm text-ink-subtle">{suffix}</span>
                )}
                {unit && hasValue && (
                    <span className={`text-lg ${tokens.text}`}>{unit}</span>
                )}
            </p>

            {percent !== null && (
                <div
                    className="mt-md h-[0.6rem] overflow-hidden rounded-pill bg-surface-sunken"
                    role="presentation"
                >
                    <div
                        className={`dash-bar h-full rounded-pill ${tokens.bar}`}
                        style={{ "--w": `${percent}%` }}
                    />
                </div>
            )}
        </div>
    );
}

/** Labelled horizontal meter. Announces its value to assistive tech. */
export function ProgressMeter({ label, value, max = 100, suffix, tone = "primary" }) {
    const tokens = toneOf(tone);
    const hasValue = value !== null && value !== undefined;
    const percent = hasValue ? Math.min(Math.round((value / max) * 100), 100) : 0;

    return (
        <div>
            <div className="mb-xs flex items-baseline justify-between gap-sm">
                <span className="text-sm text-ink-muted">{label}</span>
                <span className={`text-sm font-semibold tabular-nums ${tokens.text}`}>
                    {hasValue ? `${value.toLocaleString("fa-IR")}${suffix ?? ""}` : "—"}
                </span>
            </div>
            <div
                className="h-[0.8rem] overflow-hidden rounded-pill bg-surface-sunken"
                role="progressbar"
                aria-label={label}
                aria-valuenow={hasValue ? value : undefined}
                aria-valuemin={0}
                aria-valuemax={max}
            >
                <div
                    className={`dash-bar h-full rounded-pill ${tokens.bar}`}
                    style={{ "--w": `${percent}%` }}
                />
            </div>
        </div>
    );
}

/** Shown instead of an empty list - never leave a panel blank. */
export function EmptyState({ icon: Icon, title, description, actionLabel, actionTo }) {
    return (
        <div className="flex flex-col items-center gap-xs px-md py-2xl text-center">
            {Icon && (
                <span className="flex h-[4.8rem] w-[4.8rem] items-center justify-center rounded-pill bg-surface-sunken text-ink-subtle">
                    <Icon sx={{ fontSize: "2.4rem" }} />
                </span>
            )}
            <p className="text-base font-medium text-ink">{title}</p>
            {description && (
                <p className="max-w-[36rem] text-sm text-ink-subtle">{description}</p>
            )}
            {actionTo && (
                <Link
                    to={actionTo}
                    className="mt-xs rounded-soft bg-primary px-lg py-xs text-base text-white transition-colors duration-150 hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                    {actionLabel}
                </Link>
            )}
        </div>
    );
}

/** Shimmer placeholder used while the dashboard endpoints resolve. */
export function Skeleton({ className = "" }) {
    return (
        <div
            aria-hidden="true"
            className={`dash-shimmer rounded-soft bg-surface-sunken ${className}`}
        />
    );
}

/**
 * The top of every student page.
 *
 * Replaces three separate ad-hoc <Header> components that each centred a bare
 * <h1> with different padding. Page titles are start-aligned now: centred
 * headings over start-aligned content gave every page two competing axes.
 *
 * `note` carries the advisory text some pages had baked into their heading
 * (the classes page put an attendance rule inside its <h1> block), keeping it
 * visible but out of the title hierarchy.
 *
 * The primary action is either a link (`actionTo`, student pages) or a button
 * (`onAction`, admin pages whose CTA opens a modal). `actionIcon` prefixes it -
 * admin "add" buttons carry a + glyph. Anything richer can still come in as
 * `children`.
 */
export function PageHeader({
    title,
    description,
    icon: Icon,
    note,
    actionLabel,
    actionTo,
    onAction,
    actionIcon: ActionIcon,
    children,
}) {
    const actionClass =
        "inline-flex shrink-0 items-center gap-2xs rounded-soft bg-primary px-lg py-sm text-base text-white transition-colors duration-150 hover:bg-primary-hover active:bg-primary-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none";

    return (
        <header className="dash-rise flex flex-col gap-md pt-xl" style={{ "--i": 0 }}>
            <div className="flex flex-wrap items-start justify-between gap-md">
                <div className="flex min-w-0 items-center gap-sm">
                    {Icon && (
                        <span className="flex h-[4rem] w-[4rem] shrink-0 items-center justify-center rounded-card bg-primary-soft text-primary">
                            <Icon sx={{ fontSize: "2.2rem" }} />
                        </span>
                    )}
                    <div className="min-w-0">
                        <h1 className="text-2xl font-semibold text-ink">{title}</h1>
                        {description && (
                            <p className="mt-2xs text-base text-ink-muted">
                                {description}
                            </p>
                        )}
                    </div>
                </div>
                {actionTo && (
                    <Link to={actionTo} className={actionClass}>
                        {ActionIcon && <ActionIcon sx={{ fontSize: "1.8rem" }} />}
                        {actionLabel}
                    </Link>
                )}
                {onAction && (
                    <button
                        type="button"
                        onClick={onAction}
                        className={actionClass}
                    >
                        {ActionIcon && <ActionIcon sx={{ fontSize: "1.8rem" }} />}
                        {actionLabel}
                    </button>
                )}
                {children}
            </div>

            {note && (
                <p className="rounded-card border border-rule-subtle bg-surface-sunken p-md text-sm text-ink-muted">
                    {note}
                </p>
            )}
        </header>
    );
}

/**
 * A row action in an admin table: edit, delete, download, and friends.
 *
 * Replaces the bare `<EditOutlined onClick className="cursor-pointer" />` that
 * every page repeated - those were divs to a screen reader, had no label, and
 * no hit target beyond the glyph. This is a real <button> with an aria-label
 * and a tone-tinted hover. `danger` for delete, `primary`/`neutral` otherwise.
 */
export function TableAction({ icon: Icon, onClick, label, tone = "neutral", disabled }) {
    const hover =
        tone === "danger"
            ? "hover:bg-danger-soft hover:text-danger"
            : tone === "primary"
            ? "hover:bg-primary-soft hover:text-primary"
            : "hover:bg-surface-sunken hover:text-ink";

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
            title={label}
            className={`inline-flex h-[3.2rem] w-[3.2rem] items-center justify-center rounded-soft text-ink-muted transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40 motion-reduce:transition-none ${hover}`}
        >
            <Icon sx={{ fontSize: "1.9rem" }} />
        </button>
    );
}

/**
 * Status pill. Always renders a word, never colour alone, so state survives
 * greyscale and colour-blindness (WCAG 1.4.1).
 */
export function StatusBadge({ label, tone = "neutral", icon: Icon }) {
    const tokens = toneOf(tone);
    return (
        <span
            className={`inline-flex shrink-0 items-center gap-2xs rounded-pill px-md py-2xs text-xs font-medium ${tokens.soft} ${tokens.text}`}
        >
            {Icon && <Icon sx={{ fontSize: "1.4rem" }} />}
            {label}
        </span>
    );
}

/**
 * Generalised list row: coloured rail, title/subtitle, trailing meta.
 * Renders as a link when `to` is given, a button when `onClick` is, and a plain
 * div otherwise - so lists of items that are not navigable do not become fake
 * links.
 */
export function ListRow({ title, subtitle, meta, tone = "neutral", to, onClick, children }) {
    const tokens = toneOf(tone);
    const className =
        "group flex w-full items-center gap-md rounded-card border border-rule-subtle bg-surface p-md text-start transition-colors duration-150 hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none";

    const inner = (
        <>
            <span
                aria-hidden="true"
                className={`h-[3.2rem] w-[3px] shrink-0 rounded-pill ${tokens.bar}`}
            />
            <span className="min-w-0 flex-1">
                <span className="block truncate text-base text-ink">{title}</span>
                {subtitle && (
                    <span className="block truncate text-sm text-ink-subtle">
                        {subtitle}
                    </span>
                )}
            </span>
            {meta && <span className="shrink-0 text-end">{meta}</span>}
            {children}
        </>
    );

    if (to) {
        return (
            <Link to={to} className={className}>
                {inner}
            </Link>
        );
    }
    if (onClick) {
        return (
            <button type="button" onClick={onClick} className={className}>
                {inner}
            </button>
        );
    }
    return <div className={className}>{inner}</div>;
}
